import {
  DialogBody,
  DialogClose,
  DialogFooter,
  DialogHeader,
} from '@src/design-system/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@src/design-system/tabs';

import { Formik } from 'formik';
import type { FC } from 'react';
import { useRef, useState } from 'react';
import { Button } from '@src/design-system/button';

import {
  createUniversityInitialValues,
  importUniversitiesFormSchema,
  universityFormSchema,
} from '../../tables/helper';
import { useDialogContext } from '@src/design-system/dialog/use-dialog-context';
import universitiesApi, {
  type TUniversityPayload,
} from '../../services/universities-api';
import type { TUniversity } from '../../types';
import CreateUniversityForm from './create-university-form';
import ImportUniversitiesForm from './import-universities-form';
import type { TMember } from '@src/shared/types/member';
import { cn } from '@src/design-system/helpers';
import type { TImage } from '@src/shared/types/image';
import { omitEmpty } from '@src/shared/utils/omit-empty';

const CreateUniversityModal: FC<{ university?: TUniversity }> = ({
  university,
}) => {
  // const [reset, setReset] = useState<() => void>();
  const [currentTab, setCurrentTab] = useState<'single' | 'import'>('single');
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const { mutate: createUniversity, isPending: isCreatingUniversity } =
    universitiesApi.useCreate();
  const { mutate: updateUniversity, isPending: isUpdatingUniversity } =
    universitiesApi.useUpdate();
  const {
    mutate: importUniversitiesFromFile,
    isPending: isImportingUniversities,
  } = universitiesApi.useImportUniversities();

  const handleCreateUniversity = (values: TUniversityPayload) => {
    if (currentTab === 'import') {
      importUniversitiesFromFile((values as { file: File }).file, {
        onSuccess: () => {
          buttonRef.current?.click();
        },
      });
      return;
    }

    if (university) {
      try {
        const updatePayload = {
          id: university.id,
          ...values,
          logo:
            values.logo &&
            typeof values.logo === 'object' &&
            'id' in values.logo
              ? values.logo.id
              : values.logo,
          manager_member:
            values.manager_member && typeof values.manager_member === 'object'
              ? values.manager_member.id
              : null,
        };
        updateUniversity(updatePayload as TUniversityPayload & { id: number }, {
          onSuccess: () => {
            buttonRef.current?.click();
          },
        });
      } catch (e) {
        console.error('Payload build failed:', e);
      }

      return;
    }

    //create university
    const payload = {
      ...values,
      manager_member: values.manager_member
        ? (values.manager_member as TMember).id
        : undefined,
      logo:
        typeof values.logo === 'object'
          ? (values.logo as TImage).id
          : values.logo,
    };
    createUniversity(omitEmpty(payload) as TUniversityPayload, {
      onSuccess: () => {
        buttonRef.current?.click();
      },
    });
  };

  const { formikRef } = useDialogContext();

  return (
    <Formik
      validationSchema={
        currentTab === 'import'
          ? importUniversitiesFormSchema
          : universityFormSchema
      }
      innerRef={formikRef}
      initialValues={createUniversityInitialValues({
        university,
        tab: currentTab,
      })}
      key={currentTab}
      onSubmit={handleCreateUniversity}
    >
      {({ submitForm }) => {
        return (
          <>
            <DialogHeader>
              {university ? 'Edit University' : 'Add New University'}
            </DialogHeader>
            <DialogBody className='flex flex-col overflow-hidden p-0'>
              {university ? (
                <div className='flex-1 min-h-0 overflow-y-auto'>
                  <CreateUniversityForm />
                </div>
              ) : (
                <Tabs
                  value={currentTab}
                  onValueChange={(value) =>
                    setCurrentTab(value as 'single' | 'import')
                  }
                  className='flex flex-col flex-1 min-h-0 overflow-hidden pt-6'
                >
                  <TabsList className='w-full shrink-0 mb-4 px-4'>
                    <TabsTrigger value='single'>Single University</TabsTrigger>
                    <TabsTrigger value='import'>
                      Multiple Universities
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent
                    value='single'
                    className='flex-1 min-h-0 overflow-y-auto mt-0 scrollbar-gray'
                  >
                    <CreateUniversityForm />
                  </TabsContent>
                  <TabsContent
                    value='import'
                    className='flex-1 min-h-0 overflow-y-auto mt-0'
                  >
                    <ImportUniversitiesForm />
                  </TabsContent>
                </Tabs>
              )}
            </DialogBody>

            <DialogFooter className='mt-auto'>
              <Button
                className='flex-1'
                type='button'
                disabled={
                  isCreatingUniversity ||
                  isUpdatingUniversity ||
                  isImportingUniversities
                }
                onClick={() => {
                  submitForm();
                }}
                isLoading={
                  isCreatingUniversity ||
                  isUpdatingUniversity ||
                  isImportingUniversities
                }
              >
                Save & Close
              </Button>
              <Button
                className={cn('flex-1', !!university && 'hidden')}
                type='button'
                variant='primaryOutline'
                disabled={
                  isCreatingUniversity ||
                  isUpdatingUniversity ||
                  isImportingUniversities
                }
                isLoading={
                  isCreatingUniversity ||
                  isUpdatingUniversity ||
                  isImportingUniversities
                }
                onClick={() => {
                  // setReset(() => resetForm);
                  submitForm();
                }}
              >
                Save & add Another
              </Button>
              <DialogClose asChild>
                <Button
                  className={cn('w-3/6', !university && 'hidden')}
                  variant='primaryOutline'
                  ref={buttonRef}
                >
                  Close
                </Button>
              </DialogClose>
            </DialogFooter>
          </>
        );
      }}
    </Formik>
  );
};

export default CreateUniversityModal;
